import React, {FC, lazy, Suspense, useRef, useState} from 'react';
import './DatePicker.less';

// Heavy @mui/x-date-pickers rendering is code-split into DatePickerImpl so the
// ~650 KB picker payload loads on first render instead of being bundled into the
// eager boot chunk. This thin wrapper owns the date state; DatePickerImpl does
// the MUI rendering. The chunk is cached after first load (incl. offline via SW).
const DatePickerImpl = lazy(() => import('./DatePickerImpl'));

function isValidDate(d: unknown): d is Date {
    return d instanceof Date && !isNaN(d.getTime());
}

function parseDate(defaultValue: string | Date): Date | null {
    if (!defaultValue) return null;
    if (defaultValue instanceof Date) return isValidDate(defaultValue) ? defaultValue : null;
    const parsed = new Date(defaultValue);
    return isValidDate(parsed) ? parsed : null;
}

function dateChanged(a: string | Date, b: string | Date): boolean {
    const toComparable = (v: string | Date): string | number | null => {
        if (v instanceof Date) return isNaN(v.getTime()) ? null : v.getTime();
        return v;
    };
    return toComparable(a) !== toComparable(b);
}

/**
 * Simple library-agnostic wrapper for a Date picker.
 */
const DatePicker: FC<DatePickerProps> = props => {
    const {ariaLabel, autoFocus, encrypted, name, disabled, onChange, required, useTime, useLabel} = props;
    const [date, setDate] = useState<Date>(parseDate(props.defaultValue));
    const lastDefaultValue = useRef(props.defaultValue);

    // Pick up external date changes (e.g. task completed) without useEffect.
    // React's "adjust state during render" pattern: https://react.dev/reference/react/useState#storing-information-from-previous-renders
    // eslint-disable-next-line react-hooks/refs -- intentional ref read/write during render per React docs
    if (dateChanged(lastDefaultValue.current, props.defaultValue)) {
        // eslint-disable-next-line react-hooks/refs -- see above
        lastDefaultValue.current = props.defaultValue;
        setDate(parseDate(props.defaultValue));
    }

    const handleChange = (value: Date | null) => {
        setDate(isValidDate(value) ? value : null);
    }

    const handleAccept = (value: Date | null) => {
        const safe = isValidDate(value) ? value : null;
        setDate(safe);
        if (safe) onChange(safe);
    }

    // Lightweight, no-layout-shift placeholder shown only until the picker chunk
    // resolves on first use (cached thereafter). Keeps the field's footprint.
    const fallback = <input
        className={`date-picker ${useTime ? 'useTime ' : ''}${required ? 'required' : ''}`}
        readOnly
        aria-label={ariaLabel}
        defaultValue={date ? (useTime ? date.toLocaleString() : date.toLocaleDateString()) : ''}
    />;

    return <Suspense fallback={fallback}>
        <DatePickerImpl
            ariaLabel={ariaLabel}
            autoFocus={autoFocus}
            encrypted={encrypted}
            name={name}
            disabled={disabled}
            required={required}
            useTime={useTime}
            useLabel={useLabel}
            date={date}
            onChange={handleChange}
            onAccept={handleAccept}
        />
    </Suspense>;
}

type DatePickerProps = {
    // ARIA label (for visually impaired)
    ariaLabel?: string;

    // Should this component receive initial focus?
    autoFocus?: boolean;

    // A string date value or a Date object
    defaultValue?: string | Date;

    // Disabled
    disabled?: boolean;

    // Should this value be considered private
    encrypted?: boolean;

    // Input name
    name: string;

    // Called when the component loses focus, sends a date
    onChange?: (date: Date) => void;

    // Is a value required?
    required?: boolean;

    // Should we show time
    useTime?: boolean;

    // Use the ariaLabel as (internal) label for the input?
    useLabel?: boolean;
};

export default DatePicker;
